import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Cloud, FileBox, Lock } from 'lucide-react';
import { toast } from 'sonner';
import {
  cloudSourceService,
  runOAuthPopup,
  CloudProvider,
} from '@/services/cloudSourceService';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
import SharePointSitePickerDialog from './SharePointSitePickerDialog';

interface ConnectButtonsProps {
  driveAvailable: boolean;
  sharepointAvailable: boolean;
  hasDriveSource: boolean;
  hasSharePointSource: boolean;
  onConnected: () => void | Promise<void>;
  /**
   * 'grid' (default) lays the cards out in their own 2-column grid.
   * 'contents' dissolves the wrapper so the cards become items of the
   * CALLER's grid - used to sit them on the same row as connected sources.
   */
  layout?: 'grid' | 'contents';
}

/**
 * Authorize-connection cards (one per provider not yet connected).
 *
 * Both providers use platform-owned admin credentials (set once in
 * KB Cloud Sync Settings). End users never paste Azure App / Composio
 * credentials.
 *
 *  - Google Drive: click → save_source → start_oauth → popup → done.
 *  - SharePoint: click → save_source (no site yet) → start_oauth → popup →
 *    user signs into Microsoft → consent → tokens stored → site picker
 *    dialog → user picks from sites their account has followed → save site
 *    URL on the source → done.
 *
 * The site picker UI lives in `SharePointSitePickerDialog` and is reused
 * by `CloudSourceCard` for the "Change site" action.
 */
const CloudSourceConnectButtons: React.FC<ConnectButtonsProps> = ({
  driveAvailable,
  sharepointAvailable,
  hasDriveSource,
  hasSharePointSource,
  onConnected,
  layout = 'grid',
}) => {
  const [connecting, setConnecting] = useState<CloudProvider | null>(null);
  // After a successful SharePoint OAuth we hand the source name to the
  // shared picker dialog, which fetches the user's accessible sites and persists the
  // chosen webUrl back onto the source row.
  const [pickerSource, setPickerSource] = useState<string | null>(null);
  // Reactive admin-consent (Option B): when a standard user hits a tenant that
  // requires admin approval, we show a non-blocking notice + retry instead of
  // an error toast.
  const [adminConsentNotice, setAdminConsentNotice] = useState(false);

  const runOAuth = async (sourceName: string) => {
    const { redirect_url } = await cloudSourceService.startOAuth(sourceName);
    return runOAuthPopup(sourceName, redirect_url);
  };

  const handleConnectDrive = async () => {
    if (!driveAvailable) {
      toast.error('Google Drive is not configured. Contact your administrator.');
      return;
    }
    setConnecting('Google Drive');
    // The source row touched by this connect; cleaned up below if OAuth is
    // abandoned. We don't gate on created-vs-reused: the backend deletes it
    // ONLY when it's still a never-authorized orphan (Pending + no account
    // email). A previously-connected source always retains its account email
    // (it's only ever set, never cleared), so reuse is safe - and this also
    // self-heals orphans left behind before this cleanup existed.
    let pendingSource: string | null = null;
    try {
      const saved = await cloudSourceService.saveSource({
        provider: 'Google Drive',
        enabled: 1,
      });
      pendingSource = saved.source;
      await runOAuth(saved.source);
      toast.success('Connected to Google Drive');
      await onConnected();
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Failed to connect Google Drive');
      logger.error('Drive connect failed', err);
      toast.error('Drive connect failed', { description: msg });
      // Closing the Google account picker without selecting an account leaves
      // the source stuck in "Pending Authorization". Remove that orphan. The
      // endpoint is a no-op once the source reached Connected or Error
      // (declined scope), so a scope-error card still surfaces.
      if (pendingSource) {
        try {
          await cloudSourceService.cancelPendingOAuth(pendingSource);
        } catch (cleanupErr) {
          logger.warn('Failed to clean up cancelled Drive connect', cleanupErr);
        }
      }
      await onConnected();
    } finally {
      setConnecting(null);
    }
  };

  const handleConnectSharePoint = async () => {
    if (!sharepointAvailable) {
      toast.error('SharePoint is not configured. Contact your administrator.');
      return;
    }
    setConnecting('SharePoint');
    setAdminConsentNotice(false);
    // Cleaned up below if OAuth is abandoned. Not gated on created-vs-reused:
    // the backend only deletes a never-authorized orphan (Pending + no account
    // email), so reusing a previously-connected source is safe.
    let pendingSource: string | null = null;
    try {
      // Step 1: upsert the SharePoint source row (no site URL yet)
      const saved = await cloudSourceService.saveSource({
        provider: 'SharePoint',
        enabled: 1,
      });
      pendingSource = saved.source;
      // Step 2: run the Microsoft OAuth popup
      const result = await runOAuth(saved.source);
      // Reactive admin-consent: tenant admin must approve before this user can
      // connect. Surface the notice + retry instead of opening the site picker.
      if (result.status === 'Awaiting Admin Approval') {
        setAdminConsentNotice(true);
        await onConnected();
        return;
      }
      toast.success('Signed in to Microsoft. Loading your SharePoint sites…');
      // Step 3: open the shared site picker dialog
      setPickerSource(saved.source);
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Failed to connect SharePoint');
      logger.error('SharePoint connect failed', err);
      toast.error('SharePoint connect failed', { description: msg });
      // Closing the Microsoft sign-in popup without finishing leaves the
      // source stuck in "Pending Authorization". Remove that orphan. No-op
      // once the source reached Connected, so a connected source mid-site-pick
      // is never touched.
      if (pendingSource) {
        try {
          await cloudSourceService.cancelPendingOAuth(pendingSource);
        } catch (cleanupErr) {
          logger.warn('Failed to clean up cancelled SharePoint connect', cleanupErr);
        }
      }
      await onConnected();
    } finally {
      setConnecting(null);
    }
  };

  const cards = [
    {
      provider: 'Google Drive' as CloudProvider,
      show: !hasDriveSource,
      available: driveAvailable,
      icon: <Cloud className="h-7 w-7 text-muted-foreground" />,
      description:
        'Authorize secure access to corporate Drive folders to enable cross-departmental knowledge indexing and search.',
      onClick: handleConnectDrive,
    },
    {
      provider: 'SharePoint' as CloudProvider,
      title: 'Microsoft SharePoint',
      show: !hasSharePointSource,
      available: sharepointAvailable,
      icon: <FileBox className="h-7 w-7 text-muted-foreground" />,
      description:
        'Connect your enterprise SharePoint sites to unify institutional knowledge and enable AI analysis of internal documentation.',
      onClick: handleConnectSharePoint,
    },
  ].filter((c) => c.show);

  return (
    <>
      {adminConsentNotice && (
        <div className="col-span-full mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
          <p className="text-sm font-medium text-amber-200">
            Your IT admin needs to approve Orgain
          </p>
          <p className="mt-1 text-xs text-amber-400">
            Your organisation requires an administrator to approve Orgain for
            SharePoint. Ask your admin to approve, then click Authorize
            Connection again.
          </p>
        </div>
      )}
      {cards.length > 0 && (
        <div
          className={
            layout === 'contents'
              ? 'contents'
              : 'grid grid-cols-1 gap-4 md:grid-cols-2'
          }
        >
          {cards.map((card) => (
            <div
              key={card.provider}
              className="flex flex-col items-center rounded-xl border border-border bg-card px-6 py-8 text-center"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-xl border border-border bg-muted/40">
                {card.icon}
              </span>
              <h3 className="mt-4 text-lg font-semibold">
                {card.title || card.provider}
              </h3>
              <p className="mt-2 mb-6 max-w-sm text-sm text-muted-foreground">
                {card.description}
              </p>
              {/* mt-auto keeps the divider + button on a common baseline across
                  cards whose descriptions wrap to different line counts. */}
              <div className="mt-auto w-full border-t border-border pt-5">
                <Button
                  className="w-full text-xs font-semibold uppercase tracking-[0.14em]"
                  disabled={!card.available || connecting !== null}
                  onClick={card.onClick}
                >
                  {connecting === card.provider ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Lock className="mr-2 h-4 w-4" />
                  )}
                  Authorize Connection
                </Button>
                {!card.available && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Not configured - contact your administrator.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {pickerSource && (
        <SharePointSitePickerDialog
          open={pickerSource !== null}
          onOpenChange={(o) => {
            if (!o) setPickerSource(null);
          }}
          sourceName={pickerSource}
          onSaved={onConnected}
          // First connect: a site MUST be picked before the source is usable,
          // so no Cancel / outside-click dismiss. (Change site stays optional.)
          mandatory
        />
      )}
    </>
  );
};

export default CloudSourceConnectButtons;
